/**
 * Create a new instance of CommonHttpService
 * @class CommonHttpService
 * @description
 * This service define a wrapper over http requests GET,POST,PUT,PATCH,DELETE
 */

import { Injectable } from '@angular/core';
import { HttpParams, HttpClient, HttpHeaders } from '@angular/common/http';

import { map } from 'rxjs/internal/operators/map';
import { Observable } from 'rxjs/internal/Observable';

import { ApiResponseModel } from '@shared/models/api-response-model';
import { MapModel } from '@shared/decorators/model-map';
import { CommonBase } from '@core/interfaces/common-base';

@Injectable({
  providedIn: 'root'
})
export class CommonHttpService {


  constructor(
    private http: HttpClient
  ) { }

  /**
   * @function get
   * @description
   * Constructs a GET request that interprets the body as a JSON object and returns the response body in a given type.
   * It will parse the the response and map it with {ApiResponseModel} to set common response format
   * @param  {string} url
   * @param  {CommonBase} [n]
   * @return {ApiResponseModel} Promise of type ApiResponseModel
   */
  get<T>(url: string, args: CommonBase = {}): Observable<T> {
    const params = new HttpParams(args);
    return this.http.get(url, { params: params })
      .pipe(
        map(data => {
          return new MapModel(ApiResponseModel).map(data);
        })
      );
  }

  /**
   * dowbload file/media stream
   * @param url 
   * @param args 
   */
  getFile<T>(url: string, args: CommonBase = {}) {
    return this.http.get(url, { responseType: 'arraybuffer' });
  }

  /**
   * @function post
   * @description
   * Constructs a wrapper over POST request that interprets the body as a JSON object and returns an observable of the response.
   * It will parse the the response and map it with {ApiResponseModel} to set common response format
   * @param  {string} url
   * @param  {CommonBase} [n]
   * @return {ApiResponseModel} Promise of type ApiResponseModel
   */
  post<T>(url: string, args: CommonBase = {}): Observable<T> {
    // const params = new HttpParams(args);
    let headers = new HttpHeaders();
    return this.http.post(url, args, {
      headers: headers
    }).pipe(
      map(data => {
        return new MapModel(ApiResponseModel).map(data);
      })
    );
  }

  /**
   * @function put
   * @description
   * Constructs a wrapper over PUT request that interprets the body as a JSON object and returns an observable of the response.
   * It will parse the the response and map it with {ApiResponseModel} to set common response format
   * @param  {string} url
   * @param {CommonBase} n
   * @return {ApiResponseModel} Promise of type ApiResponseModel
   */
  put<T>(url: string, args: CommonBase = {}): Observable<T> {
    const params = new HttpParams(args);
    return this.http.put<ApiResponseModel>(url, params)
      .pipe(
        map(data => {
          return new MapModel(ApiResponseModel).map(data);
        })
      );
  }


  /**
   * @function delete
   * @description
   * Constructs a wrapper over DELETE request and returns the response in a given type.
   * It will parse the the response and map it with {ApiResponseModel} to set common response format
   * @param  {string} url
   * @return {ApiResponseModel} Promise of type ApiResponseModel
   */
  delete<T>(url: string): Observable<T> {
    return this.http.delete<any>(url);
  }


  postExternalApis<T>(url: string, args: CommonBase = {}, accesstoken: string, headerConfig: any = { 'content-type': 'application/json' }): Observable<T> {
    return this.http.post<T>(url, args, {
      headers: {
        'Authorization': 'Bearer ' + accesstoken,
        ...headerConfig
      }
    })
  }

  getExternalApis<T>(url: string, accesstoken: string, headerConfig: any = { 'content-type': 'application/json' }): Observable<T> {
    return this.http.get<T>(url, {
      headers: {
        'Authorization': 'Bearer ' + accesstoken,
        ...headerConfig
      }
    })
  }


  putExternalApis<T>(url: string, args: CommonBase = {}, accesstoken: string, headerConfig: any = { 'content-type': 'application/json' }): Observable<T> {
    return this.http.put<T>(url, args, {
      headers: {
        'Authorization': 'Bearer ' + accesstoken,
        ...headerConfig
      }
    })
  }


}
